import React, { useState, useEffect, useCallback } from 'react';
import {
    TrendingUp, MapPin, Clock, Download, ChevronRight, ChevronDown, Info,
} from 'lucide-react';
import api from '../services/api';
import '../styles/PannelloAnalisi.css';

/**
 * PannelloAnalisi — tre famiglie di indicatori, e non rispondono alla
 * stessa domanda.
 *
 * 1. Redditivita' per specialita': ricavo e margine della struttura,
 *    SEMPRE affiancati. La macro area che fattura di piu' non e'
 *    necessariamente quella che rende di piu', ed e' tutto il motivo per
 *    cui questa schermata esiste: mostrare il solo ricavo la renderebbe
 *    inutile, mostrare il solo margine sarebbe fuorviante nell'altro
 *    verso.
 * 2. Provenienza dei pazienti: l'unica cosa vera oggi, perche' le
 *    anagrafiche sono reali mentre le fatture sono di collaudo.
 * 3. Tempi del percorso: scritti, e spenti finche' le osservazioni non
 *    bastano.
 *
 * Qui non c'e' e non deve esserci nessun indicatore per singola persona
 * dello staff: il raggruppamento per macro area da' la stessa decisione
 * senza misurare le persone.
 */

const euro = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
}));

const numero = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT'));

const pct = (v) => (v == null ? '—' : `${String(v).replace('.', ',')}%`);

/** Il delta col periodo precedente: il segno si scrive, non si deduce. */
const delta = (v) => {
    const n = Number(v || 0);
    if (n === 0) return <span className="pa-delta pa-pari">uguale</span>;
    return (
        <span className={`pa-delta ${n > 0 ? 'pa-su' : 'pa-giu'}`}>
            {n > 0 ? '+' : '−'}{euro(Math.abs(n))}
        </span>
    );
};

const primoDelMese = () => {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), 1)).toISOString().slice(0, 10);
};
const ultimoDelMese = () => {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth() + 1, 0)).toISOString().slice(0, 10);
};

const scarica = async (percorso, nomeFile, setErrore) => {
    try {
        const r = await api.get(percorso, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${nomeFile}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    } catch (e) { setErrore('Non riesco a esportare il file.'); }
};

/* ---------- 1. redditivita' ---------- */

const Redditivita = ({ dati, setErrore, query }) => {
    const [aperta, setAperta] = useState(null);
    if (!dati) return <p className="pa-stato">Caricamento…</p>;

    const t = dati.totali;
    const massimo = Math.max(...dati.aree.map((a) => Number(a.ricavo)), 1);

    return (
        <>
            {/* L'avviso sui dati di collaudo usa la stessa dichiarazione
                del grafico dell'andamento, letta dallo stesso parametro:
                due dichiarazioni separate divergerebbero, e quella
                sbagliata farebbe prendere una decisione su numeri finti. */}
            {dati.dati_reali_dal && new Date(dati.dati_reali_dal) > new Date(dati.periodo.al) && (
                <p className="pa-avviso">
                    <Info size={15} />
                    <span>
                        I numeri di questo periodo sono <b>di collaudo</b>: la clinica
                        ha dichiarato che i dati diventano veri dal{' '}
                        {new Date(dati.dati_reali_dal).toLocaleDateString('it-IT')}.
                        Servono a verificare che i conti tornino, non a decidere.
                    </span>
                </p>
            )}

            {/* La scadenza resa visibile: il margine non comprende le
                spettanze delle sedute di pacchetto. Finche' sono zero
                l'avviso non c'e'; alla prima seduta compare, con il
                numero. Il calcolo non cambia da qui. */}
            {dati.sedute_pacchetto && dati.sedute_pacchetto.sedute > 0 && (
                <p className="pa-avviso">
                    <Info size={15} />
                    <span>
                        Nel periodo sono state eseguite <b>{dati.sedute_pacchetto.sedute} sedute di
                        pacchetto</b>, per {euro(dati.sedute_pacchetto.spettanza_esclusa)} di
                        spettanze: <b>il margine qui sotto non le comprende</b>, perché il ricavo
                        di una seduta è stato fatturato quando il pacchetto è stato venduto.
                        Va deciso come attribuirle, e la decisione è del commercialista.
                    </span>
                </p>
            )}

            <div className="pa-numeroni">
                <div>
                    <span>Ricavo del periodo</span>
                    <b>{euro(t.ricavo)}</b>
                    <em>{t.righe} righe di fattura</em>
                </div>
                <div>
                    <span>Margine della struttura</span>
                    <b>{euro(t.margine)}</b>
                    <em>{pct(t.margine_pct)} del ricavo</em>
                </div>
                <div className="pa-sottratti">
                    <span>Tolti per arrivarci</span>
                    <em>costo dei prodotti {euro(t.costo_prodotto)}</em>
                    <em>spettanze dei medici {euro(t.spettanza)}</em>
                </div>
            </div>

            <p className="pa-nota">
                Il margine della struttura è il ricavo meno il costo dei prodotti e
                meno la spettanza del medico: è quello che resta alla clinica.
                <b> Non è l'utile</b>: affitto, personale di struttura e utenze non
                sono qui dentro, perché non si possono attribuire a una specialità
                senza inventarsi un criterio di ripartizione.
            </p>

            <div className="pa-tabella-azioni">
                <button onClick={() => scarica(
                    `/analisi/redditivita?${query}&formato=csv`, 'redditivita-aree', setErrore)}>
                    <Download size={14} /> Macro aree
                </button>
                <button onClick={() => scarica(
                    `/analisi/redditivita?${query}&formato=csv&tabella=prestazioni`,
                    'redditivita-prestazioni', setErrore)}>
                    <Download size={14} /> Per prestazione
                </button>
            </div>

            <table className="pa-tabella">
                <thead>
                    <tr>
                        <th />
                        <th>Macro area</th>
                        <th className="pa-num">Ricavo</th>
                        <th className="pa-num">Margine struttura</th>
                        <th className="pa-num">Margine %</th>
                        <th className="pa-num">Rispetto al periodo prima</th>
                    </tr>
                </thead>
                <tbody>
                    {dati.aree.map((a) => (
                        <React.Fragment key={a.area}>
                            <tr className="pa-riga-area"
                                onClick={() => setAperta(aperta === a.area ? null : a.area)}>
                                <td className="pa-freccia">
                                    {aperta === a.area
                                        ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                                </td>
                                <td>
                                    <b>{a.area}</b>
                                    {/* La barra è sul ricavo: dà la proporzione fra
                                        le aree a colpo d'occhio, e il margine
                                        accanto dice se quella proporzione basta. */}
                                    <span className="pa-barra">
                                        <i style={{ width: `${(Number(a.ricavo) / massimo) * 100}%` }} />
                                    </span>
                                </td>
                                <td className="pa-num">{euro(a.ricavo)}</td>
                                <td className="pa-num"><b>{euro(a.margine)}</b></td>
                                <td className="pa-num">{pct(a.margine_pct)}</td>
                                <td className="pa-num">{delta(a.delta_ricavo)}</td>
                            </tr>
                            {aperta === a.area && a.prestazioni.map((p) => (
                                <tr key={p.prestazione} className="pa-riga-prestazione">
                                    <td />
                                    <td>{p.prestazione} <em>×{p.righe}</em></td>
                                    <td className="pa-num">{euro(p.ricavo)}</td>
                                    <td className="pa-num">{euro(p.margine)}</td>
                                    <td className="pa-num">{pct(p.margine_pct)}</td>
                                    <td />
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                    {!dati.aree.length && (
                        <tr><td colSpan={6} className="pa-vuoto">
                            Nessuna fattura emessa in questo periodo.
                        </td></tr>
                    )}
                </tbody>
            </table>

            <p className="pa-nota">
                Una riga scritta a mano in fattura non ha una prestazione di catalogo
                e quindi non ha macro area: finisce sotto <b>Non attribuito</b>, invece
                di sparire dal totale e far tornare i conti sbagliati.
            </p>
        </>
    );
};

/* ---------- 2. provenienza ---------- */

const Provenienza = ({ dati, setErrore }) => {
    const [vista, setVista] = useState('comuni');
    if (!dati) return <p className="pa-stato">Caricamento…</p>;

    const c = vista === 'comuni' ? dati.comuni : dati.cap;
    const massimo = Math.max(...c.elenco.map((x) => x.pazienti), 1);

    return (
        <>
            <p className="pa-nota">
                Su <b>{numero(dati.totale_pazienti)}</b> anagrafiche. È l'unico
                indicatore che oggi mostra qualcosa di vero: i pazienti sono reali,
                le fatture no. Serve a decidere dove ha senso fare pubblicità.
            </p>

            <div className="pa-tabella-azioni">
                <div className="pa-interruttore">
                    <button className={vista === 'comuni' ? 'attivo' : ''}
                        onClick={() => setVista('comuni')}>Per comune</button>
                    <button className={vista === 'cap' ? 'attivo' : ''}
                        onClick={() => setVista('cap')}>Per CAP</button>
                </div>
                <button onClick={() => scarica(
                    `/analisi/provenienza?formato=csv&tabella=${vista}`,
                    `provenienza-${vista}`, setErrore)}>
                    <Download size={14} /> CSV
                </button>
            </div>

            <table className="pa-tabella">
                <thead>
                    <tr>
                        <th>{vista === 'comuni' ? 'Comune' : 'CAP'}</th>
                        <th>{vista === 'comuni' ? 'Provincia' : 'Comune prevalente'}</th>
                        <th className="pa-num">Pazienti</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {c.elenco.map((x) => (
                        <tr key={vista === 'comuni' ? x.comune : x.cap}>
                            <td>{vista === 'comuni' ? x.comune : x.cap}</td>
                            <td className="pa-secondaria">
                                {vista === 'comuni' ? x.provincia : x.comune}
                            </td>
                            <td className="pa-num">{numero(x.pazienti)}</td>
                            <td className="pa-barra-cella">
                                <span className="pa-barra">
                                    <i style={{ width: `${(x.pazienti / massimo) * 100}%` }} />
                                </span>
                            </td>
                        </tr>
                    ))}
                    {c.altri && (
                        <tr className="pa-riga-altri">
                            <td>{c.altri.etichetta}</td>
                            <td className="pa-secondaria">{c.altri.voci} voci sotto la soglia</td>
                            <td className="pa-num">{numero(c.altri.pazienti)}</td>
                            <td />
                        </tr>
                    )}
                    {!!c.non_indicato && (
                        <tr className="pa-riga-altri">
                            <td>{vista === 'comuni' ? 'Comune non indicato' : 'CAP non indicato'}</td>
                            <td className="pa-secondaria">dato mancante in anagrafica</td>
                            <td className="pa-num">{numero(c.non_indicato)}</td>
                            <td />
                        </tr>
                    )}
                </tbody>
            </table>

            <p className="pa-avviso">
                <Info size={15} />
                <span>
                    Le voci con <b>meno di {dati.soglia} pazienti</b> sono raggruppate e
                    non si possono aprire: un comune con un solo paziente identifica
                    quella persona. Il raggruppamento lo fa il server, quindi quei nomi
                    non escono nemmeno dalla richiesta — e da qui non si arriva alla
                    scheda di nessuno.
                </span>
            </p>
        </>
    );
};

/* ---------- 3. tempi ---------- */

const VOCI_TEMPI = [
    ['ritardo', 'Ritardo sull\'orario', 'Da quando era fissato l\'appuntamento a quando il paziente è stato segnato arrivato.'],
    ['attesa', 'Attesa prima della visita', 'Da «arrivato» a «in visita».'],
    ['durata', 'Durata reale della visita', 'Da «in visita» a «completato».'],
    ['scarto_durata', 'Scarto sulla durata prevista', 'Quanto la visita sfonda, o sta dentro, la durata messa in agenda.'],
    ['permanenza', 'Permanenza in struttura', 'Dall\'arrivo all\'uscita.'],
];

const Tempi = ({ dati, setErrore, query }) => {
    if (!dati) return <p className="pa-stato">Caricamento…</p>;

    return (
        <>
            <p className="pa-nota">
                Su <b>{dati.appuntamenti}</b> appuntamenti nel periodo.
            </p>

            <div className="pa-tabella-azioni">
                <button onClick={() => scarica(
                    `/analisi/tempi?${query}&formato=csv`, 'tempi-percorso', setErrore)}>
                    <Download size={14} /> CSV
                </button>
            </div>

            <div className="pa-tempi">
                {VOCI_TEMPI.map(([chiave, titolo, spiega]) => {
                    const s = dati[chiave];
                    return (
                        <div key={chiave} className={`pa-tempo ${s.sufficiente ? '' : 'pa-spento'}`}>
                            <b>{titolo}</b>
                            <em>{spiega}</em>
                            {s.sufficiente ? (
                                <>
                                    <span className="pa-cifra">{s.media} min</span>
                                    <span className="pa-secondaria">
                                        mediana {s.mediana} · da {s.minimo} a {s.massimo} ·
                                        {' '}{s.osservazioni} osservazioni
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="pa-cifra pa-insufficiente">dati insufficienti</span>
                                    <span className="pa-secondaria">
                                        {s.osservazioni} osservazioni su {s.servono}
                                    </span>
                                </>
                            )}
                            {!!s.anomale && (
                                <span className="pa-secondaria">
                                    {s.anomale} fuori scala, escluse dal conto
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="pa-avviso">
                <Info size={15} />
                <span>
                    Sotto le <b>{dati.soglia} osservazioni</b> non si mostra una media:
                    su pochi casi non sarebbe una misura, sarebbe un numero che le
                    somiglia. Si accendono da sole quando ce ne saranno abbastanza.
                    Le osservazioni <b>fuori scala</b> — un «arrivato» segnato il giorno
                    dopo per rimediare a una dimenticanza — restano fuori dal calcolo
                    ma vengono contate: una riga scartata in silenzio è peggio di una
                    riga strana.
                </span>
            </p>
        </>
    );
};

/* ---------- la pagina ---------- */

const SEZIONI = [
    { id: 'redditivita', titolo: 'Ricavi e margine per specialità', Icona: TrendingUp },
    { id: 'provenienza', titolo: 'Da dove vengono i pazienti', Icona: MapPin },
    { id: 'tempi', titolo: 'I tempi del percorso paziente', Icona: Clock },
];

const PannelloAnalisi = () => {
    const [dal, setDal] = useState(primoDelMese);
    const [al, setAl] = useState(ultimoDelMese);
    const [sezione, setSezione] = useState('redditivita');
    const [redd, setRedd] = useState(null);
    const [prov, setProv] = useState(null);
    const [tempi, setTempi] = useState(null);
    const [errore, setErrore] = useState(null);

    const q = `dal=${dal}&al=${al}`;

    const carica = useCallback(async () => {
        try {
            setErrore(null);
            const [a, b, c] = await Promise.all([
                api.get(`/analisi/redditivita?${q}`),
                api.get('/analisi/provenienza'),
                api.get(`/analisi/tempi?${q}`),
            ]);
            setRedd(a.data.data);
            setProv(b.data.data);
            setTempi(c.data.data);
        } catch (e) {
            setErrore(e.response?.data?.message || 'Non riesco a caricare i dati.');
        }
    }, [q]);

    useEffect(() => { carica(); }, [carica]);

    return (
        <div className="pa-wrap">
            <header className="pa-testata">
                <div>
                    <h1>Analisi dei dati</h1>
                    <p>Quanto rende ogni specialità, da dove vengono i pazienti, quanto dura il percorso.</p>
                </div>
                <div className="pa-periodo">
                    <label>dal <input type="date" value={dal}
                        onChange={(e) => setDal(e.target.value)} /></label>
                    <label>al <input type="date" value={al}
                        onChange={(e) => setAl(e.target.value)} /></label>
                </div>
            </header>

            {errore && <div className="pa-errore">{errore}</div>}

            <nav className="pa-schede">
                {SEZIONI.map((s) => (
                    <button key={s.id}
                        className={sezione === s.id ? 'attiva' : ''}
                        onClick={() => setSezione(s.id)}>
                        <s.Icona size={16} /> {s.titolo}
                    </button>
                ))}
            </nav>

            <section className="pa-sezione">
                {sezione === 'redditivita'
                    && <Redditivita dati={redd} setErrore={setErrore} query={q} />}
                {sezione === 'provenienza'
                    && <Provenienza dati={prov} setErrore={setErrore} />}
                {sezione === 'tempi'
                    && <Tempi dati={tempi} setErrore={setErrore} query={q} />}
            </section>
        </div>
    );
};

export default PannelloAnalisi;
